import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchScrapeStats, fetchTier1Slugs, fetchTriageActions, unarchivePage } from '@/utils/externalDiffsAPI';

const RECENTLY_ARCHIVED_KEY = 'ps_recently_archived';

type ArchivedItem = { slug: string; name: string; archivedAt: string };

function useRecentlyArchived() {
  const [items, setItems] = useState<ArchivedItem[]>([]);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(RECENTLY_ARCHIVED_KEY);
      setItems(raw ? JSON.parse(raw) : []);
    } catch {}
  }, []);
  const remove = (slug: string) => {
    setItems(prev => {
      const updated = prev.filter(x => x.slug !== slug);
      try { sessionStorage.setItem(RECENTLY_ARCHIVED_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
  };
  return { items, remove };
}

function TriageStatusBadge({ slug, triageMap }: { slug: string; triageMap: Map<string, any> }) {
  const action = triageMap.get(slug);
  if (!action) {
    return <span className="text-xs text-gray-400">Outstanding</span>;
  }

  const meta = action.metadata as { bucket?: number; bucket_reason?: string } | null;
  const bucket = meta?.bucket;

  if (bucket === 1) {
    const taskId = action.task_id;
    const content = (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">
        ✓ Scheduled
      </span>
    );
    return taskId
      ? <Link to={`/tasks/${taskId}`} className="hover:opacity-75 transition-opacity">{content}</Link>
      : content;
  }

  if (bucket === 2 || bucket === 3) {
    return (
      <Link to={`/dashboard/scrapes/${slug}`} className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 hover:opacity-75 transition-opacity">
        ⚠ Needs review
      </Link>
    );
  }

  return <span className="text-xs text-gray-400">Outstanding</span>;
}

export default function DashboardScrapes() {
  const queryClient = useQueryClient();
  const { items: recentlyArchived, remove: removeArchived } = useRecentlyArchived();
  const [unarchivingSlug, setUnarchivingSlug] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<'all' | 'tier1' | 'tier2'>('all');
  const [searchParams] = useSearchParams();
  const initialFilter = (['outstanding', 'scheduled', 'review'].includes(searchParams.get('filter') ?? '') ? searchParams.get('filter') : 'all') as 'all' | 'outstanding' | 'scheduled' | 'review';
  const [statusFilter, setStatusFilter] = useState<'all' | 'outstanding' | 'scheduled' | 'review'>(initialFilter);

  async function handleUnarchive(slug: string) {
    if (unarchivingSlug) return;
    setUnarchivingSlug(slug);
    try {
      await unarchivePage(slug);
      removeArchived(slug);
      queryClient.setQueryData(['dashboard-scrape-stats'], (old: any) => {
        if (!old) return old;
        return { ...old, outstanding_slugs: [slug, ...(old.outstanding_slugs ?? [])], outstanding_count: (old.outstanding_count ?? 0) + 1 };
      });
    } catch {
      // silently ignore — user can retry
    } finally {
      setUnarchivingSlug(null);
    }
  }

  const { data: scrape, isLoading, error } = useQuery({
    queryKey: ['dashboard-scrape-stats'],
    queryFn: fetchScrapeStats,
    staleTime: 2 * 60 * 1000,
  });

  const { data: tier1Slugs } = useQuery({
    queryKey: ['dashboard-tier1-slugs'],
    queryFn: fetchTier1Slugs,
    staleTime: 10 * 60 * 1000,
  });

  const { data: triageMap = new Map() } = useQuery({
    queryKey: ['dashboard-triage-actions'],
    queryFn: fetchTriageActions,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const tier1Set = useMemo(() => new Set(tier1Slugs ?? []), [tier1Slugs]);

  const rows = useMemo(() => {
    const all = scrape?.outstanding_slugs ?? [];
    return all
      .filter(slug => {
        if (tierFilter === 'tier1') return tier1Set.has(slug);
        if (tierFilter === 'tier2') return !tier1Set.has(slug);
        return true;
      })
      .filter(slug => {
        if (statusFilter === 'all') return true;
        const action = triageMap.get(slug);
        const bucket = action?.metadata?.bucket;
        if (statusFilter === 'outstanding') return !bucket;
        if (statusFilter === 'scheduled') return bucket === 1;
        if (statusFilter === 'review') return bucket === 2 || bucket === 3;
        return true;
      })
      .filter(slug => !search || slug.toLowerCase().includes(search.toLowerCase()));
  }, [scrape, tier1Set, tierFilter, statusFilter, search, triageMap]);

  const counts = useMemo(() => {
    const all = scrape?.outstanding_slugs ?? [];
    return all.reduce((acc, slug) => {
      const bucket = triageMap.get(slug)?.metadata?.bucket;
      if (bucket === 1) acc.scheduled++;
      else if (bucket === 2 || bucket === 3) acc.review++;
      else acc.outstanding++;
      return acc;
    }, { outstanding: 0, scheduled: 0, review: 0 });
  }, [scrape, triageMap]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/dashboard" className="text-sm text-gray-400 hover:text-gray-600">← Dashboard</Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-lg font-semibold text-gray-900">Outstanding Scrapes</h1>
          {!isLoading && (
            <span className="ml-2 bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
              {(scrape?.outstanding_count ?? 0).toLocaleString()} total
            </span>
          )}
        </div>

        {/* Recently archived strip */}
        {recentlyArchived.length > 0 && (
          <div className="mb-4 bg-gray-800 text-white rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Recently archived</span>
              <span className="text-xs text-gray-500">— click Unarchive to undo</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {recentlyArchived.map(item => (
                <div key={item.slug} className="flex items-center gap-2 bg-gray-700 rounded-lg px-3 py-1.5">
                  <Link to={`/dashboard/scrapes/${item.slug}`} className="text-sm font-mono text-gray-200 hover:text-white">
                    {item.slug}
                  </Link>
                  <button
                    onClick={() => handleUnarchive(item.slug)}
                    disabled={unarchivingSlug === item.slug}
                    className="text-xs text-amber-300 hover:text-amber-200 disabled:opacity-50"
                  >
                    {unarchivingSlug === item.slug ? 'Restoring…' : 'Unarchive'}
                  </button>
                  <button
                    onClick={() => removeArchived(item.slug)}
                    className="text-gray-500 hover:text-gray-300 text-xs"
                    title="Dismiss"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary chips */}
        {!isLoading && triageMap.size > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button onClick={() => setStatusFilter('all')} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === 'all' ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
              All {scrape?.outstanding_count ?? 0}
            </button>
            <button onClick={() => setStatusFilter('scheduled')} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === 'scheduled' ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 text-green-700 border-green-200 hover:border-green-400'}`}>
              ✓ Scheduled {counts.scheduled}
            </button>
            <button onClick={() => setStatusFilter('review')} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === 'review' ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400'}`}>
              ⚠ Needs review {counts.review}
            </button>
            <button onClick={() => setStatusFilter('outstanding')} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === 'outstanding' ? 'bg-gray-600 text-white border-gray-600' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}>
              Not triaged {counts.outstanding}
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            {(['all', 'tier1', 'tier2'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTierFilter(t)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  tierFilter === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'all' ? 'All' : t === 'tier1' ? 'Tier 1' : 'Tier 2'}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search slugs…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="text-sm border border-gray-200 rounded px-3 py-1.5 bg-white w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {rows.length > 0 && (
            <span className="text-xs text-gray-400">{rows.length.toLocaleString()} shown</span>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 space-y-3">
              {[...Array(8)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
            </div>
          ) : error ? (
            <p className="p-8 text-sm text-red-500">Failed to load: {(error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-sm text-gray-400 italic">
              {search || tierFilter !== 'all' || statusFilter !== 'all' ? 'No slugs match your filters.' : 'No outstanding scrapes — all caught up!'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-12">#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Slug</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Tier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-40">Last processed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((slug, i) => {
                  const isTier1 = tier1Set.has(slug);
                  const action = triageMap.get(slug);
                  return (
                    <tr key={slug} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5 text-gray-400 tabular-nums">{i + 1}</td>
                      <td className="px-4 py-2.5 font-mono text-gray-800">
                        {action?.metadata?.bucket && action.metadata.bucket > 1
                          ? <Link to={`/dashboard/scrapes/${slug}`} className="hover:text-blue-600 hover:underline">{slug}</Link>
                          : slug
                        }
                      </td>
                      <td className="px-4 py-2.5">
                        {tier1Slugs ? (
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${isTier1 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isTier1 ? 'T1' : 'T2'}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <TriageStatusBadge slug={slug} triageMap={triageMap} />
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-400">
                        {action ? new Date(action.created_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
